import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ListAlt,
  SensorOccupied,
} from "@mui/icons-material";
import { DialogMode } from "../../../../types/dialogmode";
import { IContract } from "../../../../types/contract";
import { IDocument } from "../../../../types/document";
import {
  useGetContractQuery,
  useUpdateContractMutation,
} from "../../../../redux/slices/indexApiSlice";
import { ContractExtractionResult } from "../../../../types/aiOutput";
import VendorKVK from "../../vendors/components/VendorKVK";
import FraudDetection from "../../vendors/components/FraudDetection";
import VendorMetadata from "../../vendors/components/VendorMetadata";
import { IVendor } from "../../../../types/vendor";
import VendorContracts from "../../vendors/components/VendorContracts";
import {
  useGetContractsForVendorQuery,
  useGetVendorQuery,
  useLazyGetVendorOnKVKQuery,
} from "../../../../redux/slices/vendorsApiSlice";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import VendorDialog from "../../../../components/dialogs/VendorDialog";
import VendorAvatar from "../../../../components/avatars/VendorAvatar";
import KvKIcon from "../../../../icons/KvKIcon";

interface IVendorSectionProps {
  contract: IContract;
  documents: IDocument[];
  mode: DialogMode;
  aiOutputs: ContractExtractionResult[];
  hideTitle?: boolean;
}
function VendorSection({ ...props }: IVendorSectionProps) {
  const { contract, mode, documents } = props;

  const { t, i18n } = useTranslation();
  const theme = useTheme();

  const [vendor, setVendor] = useState<IVendor>();

  const { data: vendorDB } = useGetVendorQuery(
    contract.vendor?.id ?? skipToken
  );

  const { refetch: refetchContract } = useGetContractQuery(
    contract.id ?? skipToken
  );

  const [getVendorOnKVK, { data: vendorOnKVK, isSuccess: vendorOnKVKLoaded }] =
    useLazyGetVendorOnKVKQuery();

  const {
    data: vendorContracts = [],
    isLoading: contractsLoading,
    isSuccess: contractsLoaded,
    refetch: getContracts,
  } = useGetContractsForVendorQuery(vendor?.id ?? skipToken);

  const [updateContract] = useUpdateContractMutation();

  useEffect(() => {
    if (vendorDB) {
      setVendor(vendorDB);
    } else if (vendorOnKVK) {
      setVendor(vendorOnKVK);
    } else if (contract.vendor) {
      setVendor(contract.vendor);
    } else {
      setVendor(undefined);
    }
  }, [
    contract?.id,
    contract.vendor?.id,
    vendorDB,
    vendorOnKVK,
    vendorOnKVKLoaded,
  ]);

  useEffect(() => {
    // first, get the vendor based on the id
    if (contract.vendor?.id) {
      // function should start automatically
    } else if (contract.vendor?.kvkNumber) {
      // there is no id yet, so check if there is a vendor with the same kvk number
      getVendorOnKVK(contract.vendor.kvkNumber);
    }
  }, [contract.vendor?.kvkNumber]);

  const handleVendorStored = (addedVendor: IVendor) => {
    // refetch the contracts because they now have a vendor id
    refetchContract();
  };

  return (
    <Box pb={2}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
        }}
      >
        {contract?.vendor?.id ? (
          <VendorAvatar vendorId={contract?.vendor?.id} big />
        ) : (
          <VendorMetadata vendor={vendor} />
        )}

        <div>
          {!contract?.vendor?.id && (
            <VendorDialog
              mode={DialogMode.Add}
              vendor={contract.vendor}
              onSuccess={handleVendorStored}
              label={t("Save vendor")!}
            />
          )}
        </div>

        <div>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <ListItem disablePadding disableGutters>
                <ListItemIcon
                  sx={{
                    color: contract.contractCategory
                      ? theme.palette.primary.main
                      : theme.palette.secondary.main,
                  }}
                >
                  <ListAlt />
                </ListItemIcon>
                <ListItemText
                  primary={t("Contracts")}
                  secondary={t("contract_vendors.other_contracts_with_vendor", {
                    count: vendorContracts?.length,
                  })}
                />
              </ListItem>
            </AccordionSummary>
            <AccordionDetails>
              <VendorContracts
                vendor={vendor}
                vendorContracts={vendorContracts}
              />
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <ListItem disablePadding disableGutters>
                <ListItemIcon
                  sx={{
                    color: contract.contractCategory
                      ? theme.palette.primary.main
                      : theme.palette.secondary.main,
                  }}
                >
                  <KvKIcon />
                </ListItemIcon>
                <ListItemText
                  primary={t("contract_vendors.business_register")}
                  secondary={t(
                    "contract_vendors.business_register_description"
                  )}
                />
              </ListItem>
            </AccordionSummary>
            <AccordionDetails>
              <VendorKVK vendor={vendor} />
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <ListItem disablePadding disableGutters>
                <ListItemIcon
                  sx={{
                    color: contract.contractCategory
                      ? theme.palette.primary.main
                      : theme.palette.secondary.main,
                  }}
                >
                  <SensorOccupied />
                </ListItemIcon>
                <ListItemText
                  primary={t("contract_vendors.fraud_detection")}
                  secondary={t("contract_vendors.fraud_detection_description")}
                />
              </ListItem>
            </AccordionSummary>
            <AccordionDetails>
              <FraudDetection vendor={vendor} />
            </AccordionDetails>
          </Accordion>
        </div>

        {contract?.vendor?.id && (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <VendorDialog
              mode={DialogMode.Edit}
              vendor={contract.vendor}
              onSuccess={handleVendorStored}
              label={t("Edit vendor")!}
            />
          </div>
        )}
      </div>
    </Box>
  );
}

export default VendorSection;
